// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Button, Card, Form, Input, Spin, Typography, message } from "antd";
import { LeftOutlined, UserOutlined, MailOutlined, LockOutlined } from "@ant-design/icons";
import { useSmartBack } from "../../utils/useSmartBack";
import PageLogoutButton from "../../Components/PageLogoutButton";
import NavigationDropdown from "../../Components/NavigationDropDown";
import { getProfile, changePassword } from "../../utils/API";

const { Title, Text } = Typography;

const ProfilePage: React.FC = () => {
  const smartBack = useSmartBack();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<{ email?: string; userName?: string }>({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getProfile();
        if (res?.data?.success && res.data.user) {
          setProfile(res.data.user);
        } else {
          message.error(res?.data?.message || "Failed to load profile");
        }
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load profile",
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const res = await changePassword({ newPassword: values.newPassword });
      if (res?.data?.success) {
        message.success(res.data.message || "Password updated successfully");
        form.resetFields(["newPassword", "confirmPassword"]);
      } else {
        message.error(res?.data?.message || "Failed to update password");
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to update password",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="flex items-center justify-center rounded-md border-gray-200"
            onClick={smartBack}
          />
          <NavigationDropdown hideValues={["dashboard"]} />
          <Title level={4} className="!mb-0">
            Profile
          </Title>
        </div>
        <PageLogoutButton />
      </div>

      <div className="max-w-2xl mx-auto py-8 px-4">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Account details */}
            <Card
              title="Account Details"
              className="mb-6 shadow-sm"
              headStyle={{ fontWeight: 600 }}
            >
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">
                  Email
                </label>
                <Input
                  value={profile.email || ""}
                  prefix={<MailOutlined className="text-gray-400" />}
                  disabled
                />
                <Text type="secondary" className="text-xs">
                  Email cannot be changed.
                </Text>
              </div>

              {profile.userName ? (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Username
                  </label>
                  <Input
                    value={profile.userName}
                    prefix={<UserOutlined className="text-gray-400" />}
                    disabled
                  />
                </div>
              ) : null}
            </Card>

            {/* Change password */}
            <Card
              title="Change Password"
              className="shadow-sm"
              headStyle={{ fontWeight: 600 }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={false}
              >
                <Form.Item
                  label="New Password"
                  name="newPassword"
                  rules={[
                    { required: true, message: "Please enter a new password" },
                    { min: 6, message: "Password must be at least 6 characters" },
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  label="Re-type New Password"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  hasFeedback
                  rules={[
                    { required: true, message: "Please re-type the new password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("The two passwords do not match"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder="Re-type new password"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <div className="flex justify-end">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={saving}
                      className="bg-[#004aad]"
                    >
                      Update Password
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
